// src/pdf/manutenzionePdf.js
//
// Generazione del verbale di MANUTENZIONE.
// Modulo condiviso frontend/backend: riceve i byte del template PDF
// e restituisce i byte del PDF compilato. Nessun accesso al filesystem
// e nessun uso di crypto: il layout viene deciso fuori da qui (vedi
// resolveManutenzioneLayout) perche browser e Node calcolano lo sha256
// con primitive diverse.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  createWriters,
  embedSignature,
  toItalianDateString,
} from './pdfShared.js';
import { removeSupersededObjects } from './pdfSanitize.js';

/** Nome del file template in public/Manutenzione/ */
export function manutenzioneTemplateFileName(template) {
  return template === 'CIESSE_SERVICE' ? 'CiesseService.pdf' : 'CiesseImpianti.pdf';
}

/*
 * I due moduli non hanno sempre la stessa griglia, e lo stesso nome di file ha
 * gia cambiato contenuto una volta. Il layout viene quindi scelto in base al
 * contenuto del PDF e non al nome logico del template: cosi sostituire un
 * modulo non puo piu spostare in silenzio cio che ci scriviamo sopra.
 */
export const LAYOUT_GRIGLIA_CLASSICA = 'GRIGLIA_CLASSICA';
export const LAYOUT_IMPIANTI_LUGLIO_2026 = 'IMPIANTI_LUGLIO_2026';

export const LAYOUT_PER_FIRMA = {
  // CiesseService.pdf
  e768d457a4654073ac003ed557578091068b43046f64492cde804ba1b897d673:
    LAYOUT_GRIGLIA_CLASSICA,
  // CiesseImpianti.pdf in uso fino al 13/07/2026: griglia identica a Service
  '419f8596ec1e49add68f713b99e8b6ba3fa05c02df51ef582810772ec5b5094e':
    LAYOUT_GRIGLIA_CLASSICA,
  // CiesseImpianti.pdf dal 13/07/2026: righe 12-15 piu in basso, caselle 8x8
  '32e8b63811d10d893403ab86d78d4abe970300126e138d7c5f4a12b2f1d7dec9':
    LAYOUT_IMPIANTI_LUGLIO_2026,

  // Moduli riesportati l'08/09/2026 (commit d99de82): stessi file, molto piu
  // leggeri, ma la griglia non e cambiata. Verificato misurando il PDF:
  //  - CiesseImpianti: le 6 caselle delle righe 12-15 sono 8x8 pt a x=77.8 con
  //    base a 573.5 / 605.5 / 620.5 / 640.5 / 655.5 / 687 dal bordo alto, e
  //    l'etichetta "T Set" e a x=401 con baseline height-586.4. Sono i valori
  //    di LAYOUT_IMPIANTI_LUGLIO_2026.
  //  - CiesseService: le 19 righe della checklist hanno baseline 296.4 317.4
  //    338.4 365.8 386.9 407.9 428.9 449.9 470.9 485.2 506.2 520.5 541.5 562.5
  //    590 604.2 625.3 639.5 679.3, cioe la griglia classica.
  '9c831a4f2fefab07dac4c14b133f5483a50853541e033f3c55bc7aaadef0d649':
    LAYOUT_GRIGLIA_CLASSICA,
  a07a1345601de2a742cf0d45217a14f31c5cd3fe3c1eed04b342b34bdc4d9f63:
    LAYOUT_IMPIANTI_LUGLIO_2026,
};

/**
 * Sceglie il layout a partire dallo sha256 (hex) del template.
 * Chi chiama calcola la firma con la primitiva della sua piattaforma:
 * node:crypto sul server, crypto.subtle nel browser.
 *
 * @param {string|null} firma sha256 esadecimale del template, null se non calcolabile
 * @param {string} template nome logico ('CIESSE_SERVICE' | 'CIESSE_IMPIANTI')
 */
export function resolveManutenzioneLayout(firma, template) {
  const layout = firma ? LAYOUT_PER_FIRMA[firma] : null;

  if (layout) return layout;

  // Modulo mai visto: si prosegue con il layout oggi associato a quel
  // template, ma va segnalato perche le coordinate potrebbero non combaciare.
  const ripiego =
    template === 'CIESSE_SERVICE'
      ? LAYOUT_GRIGLIA_CLASSICA
      : LAYOUT_IMPIANTI_LUGLIO_2026;

  console.warn(
    '[MANUTENZIONE_PDF] modulo non riconosciuto: le posizioni potrebbero non ' +
      'combaciare. Verificare il verbale e aggiungere la firma a ' +
      'LAYOUT_PER_FIRMA.',
    { template, firma, layoutDiRipiego: ripiego }
  );

  return ripiego;
}

/**
 * @param {Object} params
 * @param {Uint8Array|ArrayBuffer} params.templateBytes byte del PDF template
 * @param {string} params.layout uno dei LAYOUT_* (vedi resolveManutenzioneLayout)
 * @param {Object} params.formData
 * @param {Object} params.checks
 * @param {string=} params.tecnicoSignature data URL PNG
 * @param {string=} params.clienteSignature data URL PNG
 * @returns {Promise<Uint8Array>}
 */
export async function buildManutenzionePdf({
  templateBytes,
  layout,
  formData = {},
  checks = {},
  tecnicoSignature,
  clienteSignature,
}) {
  const pdfDoc = await PDFDocument.load(templateBytes);
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const { width, height } = firstPage.getSize();

  const { writeText, drawX, writeMultiline } = createWriters(firstPage, font, {
    fontSize: 10,
    color: rgb(0, 0, 0),
  });

  /* ========= 1) DATI INTERVENTO ========= */
  const {
    data,
    allegatoAlVit,
    modello,
    inventario,
    matricola,
  } = formData;

  const dataIt = toItalianDateString(data);

  writeText(dataIt || '', 270, height - 778);
  writeText(dataIt || '', 105, height - 222);
  writeText(modello || '', 120, height - 236);
  writeText(matricola || '', 350, height - 236);
  writeText(inventario || '', 453, height - 65);

  // Campo "Allegato al VIT n.": ci va solo allegatoAlVit. Prima qui
  // veniva scritto anche luogo, alla stessa identica coordinata, e con
  // entrambi compilati i due testi si stampavano uno sull'altro.
  writeText(allegatoAlVit || '', 410, height - 222);

  /*
   * Coordinate di compilazione per LAYOUT_GRIGLIA_CLASSICA: 19 caselle da
   * 10.1 x 10.1 pt a x=76.7. E la griglia di CiesseService.pdf e anche
   * quella di CiesseImpianti.pdf fino al 13/07/2026.
   *
   * Le righe 12-15 vengono ridefinite piu sotto per LAYOUT_IMPIANTI_LUGLIO_2026.
   */
  const checkPositions = {
    1: { ok: { x: 78, y: height - 296 } },
    2: { ok: { x: 78, y: height - 317 } },
    3: { ok: { x: 78, y: height - 339 } },
    4: { ok: { x: 78, y: height - 365 } },
    5: { ok: { x: 78, y: height - 386 } },
    6: { ok: { x: 78, y: height - 407 } },
    7: { ok: { x: 78, y: height - 428 } },
    8: {
      ok: { x: 78, y: height - 449 },
      note: { x: 310, y: height - 449 },
    },
    9: {
      ok: { x: 78, y: height - 471 },
      na: { x: 78, y: height - 485 },
      value: { x: 310, y: height - 478 },
    },
    10: {
      ok: { x: 78, y: height - 506 },
      na: { x: 78, y: height - 520 },
    },
    11: { ok: { x: 78, y: height - 541 } },
    12: {
      ok: { x: 78, y: height - 562 },
      tSet: { x: 410, y: height - 570 },
      tMis: { x: 480, y: height - 570 },
    },
    13: {
      ok: { x: 78, y: height - 591 },
      na: { x: 78, y: height - 605 },
      urSet: { x: 400, y: height - 596 },
      urMis: { x: 480, y: height - 596 },
    },
    14: {
      ok: { x: 78, y: height - 625 },
      na: { x: 78, y: height - 639 },
    },
    15: {
      ok: { x: 78, y: height - 679 },
      // la cella ha 4 righe pre-stampate a 174.6 / 164.2 / 153.9 / 143.5
      note: { x: 148, y: height - 667, floorY: 141.5 },
    },
  };

  /*
   * LAYOUT_IMPIANTI_LUGLIO_2026.
   *
   * CiesseImpianti.pdf e stato sostituito il 13/07/2026 con un modulo in cui
   * il testo della riga 12 e passato da 1 a 4 righe. Di conseguenza:
   *
   *  - le righe 1-11 sono rimaste nella stessa identica posizione;
   *  - le righe 12-15 sono scese di 8-17 pt;
   *  - le loro caselle sono diventate 8 x 8 pt a x=77.8, invece di
   *    10.1 x 10.1 a x=76.7 (da cui il corpo ridotto della X);
   *  - la riga "T Set _______ e T Min _______" e stata ridisegnata dentro
   *    il PDF a x=401 con baseline height-586.4, spostando i due spazi da
   *    compilare a 420-448 e 482-510;
   *  - gli spazi "UR% Set" e "UR% Mis" della riga 13 sono a 366-397 e
   *    444-475, invece di 394-429 e 476-511.
   *
   * Valori misurati sul modulo. Gli altri moduli non sono interessati.
   */
  if (layout === LAYOUT_IMPIANTI_LUGLIO_2026) {
    checkPositions[12] = {
      ok: { x: 78, y: height - 573, size: 10 },
      tSet: { x: 422, y: height - 586 },
      tMis: { x: 484, y: height - 586 },
    };

    checkPositions[13] = {
      ok: { x: 78, y: height - 605, size: 10 },
      na: { x: 78, y: height - 620, size: 10 },
      urSet: { x: 368, y: height - 613 },
      urMis: { x: 446, y: height - 613 },
    };

    checkPositions[14] = {
      ok: { x: 78, y: height - 640, size: 10 },
      na: { x: 78, y: height - 655, size: 10 },
    };

    checkPositions[15] = {
      ok: { x: 78, y: height - 686, size: 10 },
      // qui l'etichetta "Altro" e a y=168.5 e la cella finisce sulla riga
      // di tabella a y=138.5: c'e spazio per due righe
      note: { x: 148, y: height - 684, floorY: 141.5 },
    };
  }

  Object.entries(checkPositions).forEach(([index, cfg]) => {
    const dataCheck = checks[index];
    if (!dataCheck) return;

    if (dataCheck.ok && cfg.ok) {
      drawX(cfg.ok.x, cfg.ok.y, cfg.ok.size);
    }

    if (dataCheck.na && cfg.na) {
      drawX(cfg.na.x, cfg.na.y, cfg.na.size);
    }

    if (index === '8' && dataCheck.note && cfg.note) {
      writeText(dataCheck.note, cfg.note.x, cfg.note.y);
    }

    if (index === '9' && dataCheck.value && cfg.value) {
      writeText(dataCheck.value, cfg.value.x, cfg.value.y);
    }

    if (index === '12') {
      if (dataCheck.tSet && cfg.tSet) {
        writeText(dataCheck.tSet, cfg.tSet.x, cfg.tSet.y);
      }
      if (dataCheck.tMis && cfg.tMis) {
        writeText(dataCheck.tMis, cfg.tMis.x, cfg.tMis.y);
      }
    }

    if (index === '13') {
      if (dataCheck.urSet && cfg.urSet) {
        writeText(dataCheck.urSet, cfg.urSet.x, cfg.urSet.y);
      }
      if (dataCheck.urMis && cfg.urMis) {
        writeText(dataCheck.urMis, cfg.urMis.x, cfg.urMis.y);
      }
    }

    if (index === '15' && dataCheck.note && cfg.note) {
      writeMultiline(dataCheck.note, cfg.note.x, cfg.note.y, {
        maxWidth: 320,
        lineHeight: 11,
        floorY: cfg.note.floorY,
      });
    }
  });

  /* ========= 3) FIRME ========= */
  await embedSignature(pdfDoc, firstPage, clienteSignature, width - 210, 40, 150, 60);
  await embedSignature(pdfDoc, firstPage, tecnicoSignature, 75, 40, 150, 60);

  // Va richiamata subito prima del save: vedi pdfSanitize.js
  removeSupersededObjects(pdfDoc);

  return pdfDoc.save();
}
